// COMP-NOTIFICATION-CENTER — زنگوله مرکز اعلان کارکنان (US-MSG-060، AC-MSG-002.1؛ AP-MSG-01): شمار خوانده‌نشده، فهرست با نوع، شدت (Info،
// Warning، Critical)، زمان، لینک و وضعیت؛ «خواندن همه»؛ اعلان Critical تأییدنشده نوار قرمز ثابت با «تأیید مشاهده» دارد (تکرار تا تأیید —
// BR-MSG-060). به‌روزرسانی زنده با SignalR (`/hubs/notifications`، فقط Long Polling تا توکن در سرآیند Authorization بماند — C-AGENT-477) و
// پشتیبان Polling هر ۶۰ ثانیه. پیام زنده فقط شناسه/نوع/شدت دارد و فهرست از API خوانده می‌شود. AP-MSG-02: تنظیمات اعلان به ازای نوع و
// Web Push این دستگاه (US-MSG-061، US-MSG-002).
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { unwrap, type NotificationView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, faDigits, JalaliDate, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { COMP_NOTIFICATION_CENTER, msgKeys } from './common';
import { NotificationPreferencesDialog } from './NotificationPreferences';

export const NOTIFICATION_HUB_PATH = '/hubs/notifications';
const POLL_MS = 60_000;

export interface NotificationRealtimeConnection {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  onNotification: (handler: () => void) => void;
}

export type NotificationRealtimeFactory = (options: {
  url: string;
  getToken: () => string | undefined;
}) => NotificationRealtimeConnection;

/** اتصال واقعی SignalR (Long Polling، تلاش دوباره خودکار). */
export const signalRNotificationFactory: NotificationRealtimeFactory = ({ url, getToken }) => {
  const connection = new HubConnectionBuilder()
    .withUrl(url, { transport: HttpTransportType.LongPolling, accessTokenFactory: () => getToken() ?? '' })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();
  return {
    start: () => connection.start(),
    stop: () => connection.stop(),
    onNotification: (handler) => connection.on('notification', handler),
  };
};

export const NotificationRealtimeContext = createContext<{
  apiBaseUrl: string;
  factory: NotificationRealtimeFactory | false;
}>({
  apiBaseUrl: '',
  factory: false,
});

function useNotificationRealtime(onSignal: () => void) {
  const { apiBaseUrl, factory } = useContext(NotificationRealtimeContext);
  const { session } = useTenantSession();
  const token = useRef<string | undefined>(undefined);
  token.current = session?.accessToken;
  const handler = useRef(onSignal);
  handler.current = onSignal;
  const tenantId = session?.tenantId;

  useEffect(() => {
    if (factory === false || !tenantId) {
      return undefined;
    }
    const base = apiBaseUrl || (typeof window === 'undefined' ? '' : window.location.origin);
    const connection = factory({ url: `${base}${NOTIFICATION_HUB_PATH}`, getToken: () => token.current });
    connection.onNotification(() => handler.current());
    connection.start().catch(() => undefined);
    return () => {
      void connection.stop().catch(() => undefined);
    };
  }, [apiBaseUrl, factory, tenantId]);
}

function useNotificationText() {
  const { t } = useTranslation('msg');
  return (item: NotificationView) =>
    t(`msg:notification_types.${item.type.replaceAll('.', '_')}`, { ...item.args, defaultValue: item.type });
}

/** نوار قرمز اعلان Critical تأییدنشده (AC-MSG-060.2). */
export function CriticalNotificationBanner() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const text = useNotificationText();
  const summary = useQuery({
    queryKey: msgKeys.summary(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/notifications/summary')),
    enabled: Boolean(session?.tenantId),
    refetchInterval: POLL_MS,
    retry: false,
  });
  const acknowledge = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/messaging/notifications/{id}:acknowledge', { params: { path: { id } } }),
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: msgKeys.notifications(session?.tenantId) }),
  });
  const critical = summary.data?.critical;
  if (!critical) {
    return null;
  }
  return (
    <Alert
      severity="error"
      variant="filled"
      data-testid="critical-notification-banner"
      action={
        <Stack direction="row" spacing={1}>
          {critical.link ? (
            <Button color="inherit" size="small" onClick={() => navigate(critical.link ?? '/')}>
              {t('msg:notifications.open')}
            </Button>
          ) : null}
          <Button
            color="inherit"
            size="small"
            onClick={() => acknowledge.mutate(critical.id)}
            disabled={acknowledge.isPending}
            data-testid="critical-notification-ack"
          >
            {t('msg:notifications.acknowledge')}
          </Button>
        </Stack>
      }
    >
      {text(critical)}
    </Alert>
  );
}

/** زنگوله و فهرست اعلان‌ها (Header اپ کارکنان). */
export function NotificationBell() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const text = useNotificationText();
  const popoverId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const tenantId = session?.tenantId;
  const summary = useQuery({
    queryKey: msgKeys.summary(tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/notifications/summary')),
    enabled: Boolean(tenantId),
    refetchInterval: POLL_MS,
    retry: false,
  });
  const list = useQuery({
    queryKey: msgKeys.notifications(tenantId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/messaging/notifications', { params: { query: { limit: 20 } } })),
    enabled: anchor !== null,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: msgKeys.notifications(tenantId) });
  useNotificationRealtime(refresh);

  const read = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/messaging/notifications/{id}:read', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/messaging/notifications:read-all')),
    onSuccess: refresh,
  });

  const unread = Number(summary.data?.unread ?? 0);
  const open = (item: NotificationView) => {
    if (item.status === 'Unread') {
      read.mutate(item.id);
    }
    if (item.link) {
      setAnchor(null);
      navigate(item.link);
    }
  };

  return (
    <>
      <Tooltip title={t('msg:notifications.title')}>
        <IconButton
          aria-label={t('msg:notifications.bell', { count: unread })}
          aria-haspopup="dialog"
          aria-controls={anchor ? popoverId : undefined}
          onClick={(event) => setAnchor(event.currentTarget)}
          data-testid="notification-bell"
          data-unread={String(unread)}
        >
          <Badge badgeContent={unread > 0 ? faDigits(unread) : undefined} color="error">
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={popoverId}
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { width: 360, maxWidth: '100vw' },
            role: 'dialog',
            'aria-label': t('msg:notifications.title'),
          } as never,
        }}
      >
        <Box data-screen-id={COMP_NOTIFICATION_CENTER} sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h3" sx={{ flex: 1 }}>
              {t('msg:notifications.title')}
            </Typography>
            <Button
              size="small"
              onClick={() => readAll.mutate()}
              disabled={unread === 0 || readAll.isPending}
              data-testid="notification-read-all"
            >
              {t('msg:notifications.read_all')}
            </Button>
            <Tooltip title={t('msg:notifications.preferences')}>
              <IconButton
                size="small"
                aria-label={t('msg:notifications.preferences')}
                onClick={() => {
                  setAnchor(null);
                  setPreferencesOpen(true);
                }}
                data-testid="notification-preferences"
              >
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Divider />
          {list.isPending ? <LoadingState rows={3} /> : null}
          {list.error ? <ErrorState error={list.error} compact /> : null}
          {list.data && list.data.items.length === 0 ? (
            <EmptyState titleKey="msg:notifications.empty" />
          ) : null}
          {list.data && list.data.items.length > 0 ? (
            <List dense disablePadding>
              {list.data.items.map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => open(item)}
                    data-testid={`notification-item-${item.id}`}
                    data-status={item.status}
                    data-severity={item.severity}
                    sx={{
                      borderInlineStart: 3,
                      borderColor:
                        item.severity === 'Critical'
                          ? 'error.main'
                          : item.severity === 'Warning'
                            ? 'warning.main'
                            : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          component="span"
                          sx={{ fontWeight: item.status === 'Unread' ? 700 : 400 }}
                        >
                          {text(item)}
                        </Typography>
                      }
                      secondary={
                        <Stack component="span" direction="row" spacing={1}>
                          <span>{t(`msg:notifications.severity.${item.severity}`)}</span>
                          <JalaliDate value={item.occurredAt} withTime />
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : null}
        </Box>
      </Popover>
      {preferencesOpen ? <NotificationPreferencesDialog onClose={() => setPreferencesOpen(false)} /> : null}
    </>
  );
}

export function NotificationRealtimeProvider({
  apiBaseUrl,
  factory,
  children,
}: {
  apiBaseUrl: string;
  factory: NotificationRealtimeFactory | false;
  children: ReactNode;
}) {
  return (
    <NotificationRealtimeContext.Provider value={{ apiBaseUrl, factory }}>
      {children}
    </NotificationRealtimeContext.Provider>
  );
}
